<template>
  <v-flex xs12>
    <v-toolbar flat>
      <v-toolbar-title>Tätigkeiten</v-toolbar-title>
      <v-spacer />

      <!-- edit -->
      <v-dialog
        v-model="showEditDialog"
        :max-width="dialogWidth"
        persistent
      >
        <v-card>
          <v-card-title>
            <span
              v-if="selectedObject.id !== null"
              class="headline"
            >Bearbeiten: {{ selectedObject.name }}</span>
            <span
              v-else-if="!!selectedObject.name"
              class="headline"
            >Neue Tätigkeit: {{ selectedObject.name }}</span>
            <span
              v-else
              class="headline"
            >Neue Tätigkeit</span>
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="submitEdit">
              <v-text-field
                v-model="selectedObject.name"
                type="text"
                label="Name"
                :disabled="saving"
                :error-messages="getError('name')"
              />
              <v-checkbox
                v-model="selectedObject.inactive"
                label="Inaktiv"
                :disabled="saving"
                :error-messages="errors.inactive"
              />
              <Multiselect
                v-model="selectedObject.projects"
                label="name"
                multiple
                :options="allProjects"
                track-by="id"
                :close-on-select="false"
                placeholder="Projekte auswählen"
                select-label="Mit Eingabe-Taste auswählen"
                selected-label="Ausgewählt"
                deselect-label="Mit Eingabe-Taste abwählen"
                :disabled="ignore_project_operation_assignment"
              >
                <template v-slot:noResult>
                  Keine Suchergebnisse.
                </template>
                <template v-slot:noOptions>
                  Liste ist leer.
                </template>
              </Multiselect>

              <span v-if="ignore_project_operation_assignment">
                <b>Projekte und Tätigkeiten werden automatisch allen Mitarbeitern angezeigt. </b>
              </span>

            </v-form>
            <v-progress-linear
              v-if="saving"
              indeterminate
            />
            <v-alert type="error" :value="!!errors.validation.detail">
              {{ errors.validation.detail }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="$router.push({name: 'operations'})"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="saveEditDialog"
            >
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- confirm removal -->
      <v-dialog
        v-model="showDeleteDialog"
        :max-width="dialogWidth"
      >
        <v-card>
          <v-card-title><span class="headline">Tätigkeit wirklich löschen?</span></v-card-title>
          <v-card-text>
            <p>Möchten Sie die Tätigkeit <em>{{ selectedObject.name }}</em> wirklich löschen?</p>
            <p>Achtung: Dadurch wird bei allen Stundeneinträge, die dieser Tätigkeit zugeordnet sind, das Tätigkeitsfeld geleert.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="showDeleteDialog = false"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="deleteOperation"
            >
              Löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-toolbar-items>
        <v-btn
          icon
          :disabled="loading"
          @click="fetchItems"
        >
          <v-icon>mdi-cached</v-icon>
        </v-btn>
        <v-btn
          icon
          :disabled="loading"
          @click="openNewDialog"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <!-- actual data -->
    <v-data-table
      :headers="headers"
      :items-per-page="-1"
      :items="items"
      :loading="loading"
      fixed-header
      :height="dataTableHeight"
    >
      <template v-slot:item="props">
        <tr>
          <td>{{ props.item.name }}</td>
          <td class="text-right">
            <v-btn
              icon
              title="Bearbeiten"
              :disabled="loading"
              @click="openEditDialog(props.item)"
            >
              <v-icon small>
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              icon
              title="Löschen"
              :disabled="loading"
              @click="openDeleteDialog(props.item)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <v-alert type="error" :value="errors.unknown">{{ errors.unknown }}</v-alert>
        <span>Keine Daten.</span>
      </template>
    </v-data-table>
  </v-flex>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { api, endpoints } from '@/api'
import Multiselect from 'vue-multiselect'

import {dialogWidth, dataTableHeight} from './utils/sizes'

export default {
  name: 'ManagementOperations',
  metaInfo: {
    title: 'Tätigkeiten verwalten',
  },
  components: {
    Multiselect,
  },
  data () {
    return {
      loading: false,
      saving: false,
      items: [],
      selectedItem: null,
      showEmptyElement: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Aktion", value: "id", sortable: false, class: "text-right" },
      ],
      showEditDialog: false,
      showDeleteDialog: false,
      selectedObject: {
        id: null,
        name: "",
        projects: [],
      },
      errors: {
        validation: {},
        unknown: "",
      },
      allProjects: [],
      projectsLoading: false,
      dataTableHeight: dataTableHeight
    }
  },
  computed: {
    ...mapGetters(['selectedTenant', 'selectedTenantObj']),
    dialogWidth: dialogWidth,
    allEmployees () {
      return this.items
    },
    selectedOperationId () {
      return this.$route.params.id || null
    },
    ignore_project_operation_assignment () {
      return this.selectedTenantObj && this.selectedTenantObj.ignore_project_operation_assignment
    },
  },
  watch: {
    selectedTenant: {
      handler (new_, old) {
        this.fetchItems()
        this.fetchProjects()
      },
      immediate: true,
    },
    selectedOperationId: {
      async handler (newValue) {
        if (newValue === null) {
          this.showEditDialog = false
          return
        }
        if (newValue === "new") {
          this.selectedObject.id = null
          this.selectedObject.name = ""
          this.selectedObject.projects.splice(0)
          this.$set(this.errors, "validation", {})
          this.showEditDialog = true
        } else {
          this.loading = true
          try {
            const r = await api(endpoints.fetch_operation(this.selectedOperationId))
            if (r.ok) {
              const employee = await r.json()
              this.selectedObject.id = employee.id
              this.selectedObject.name = employee.name
              this.selectedObject.projects.splice(0, this.selectedObject.projects.length, ...employee.projects)
              this.$set(this.errors, "validation", {})
              this.showEditDialog = true
            }
          } catch (err) {
            // TODO: Error handling
          } finally {
            this.loading = false
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchItems () {
      this.loading = true
      this.errors.unknown = ""
      if (this.selectedTenant === null) {
        return
      }
      try {
        const r = await api(endpoints.fetch_operation(), {tenant: this.selectedTenant, details: true, ordering: "name"}, {method: 'GET'})
        if (r.ok) {
          const d = await r.json()
          this.items.splice(0, this.items.length, ...d)
        } else {
          this.items.splice(0)
          this.errors.unknown = (await r.json()).detail
        }
      } catch (err) {
        console.log(err)
        this.items.splice(0)
        this.errors.unknown = err.message
      } finally {
        this.loading = false
      }
    },
    openNewDialog () {
      this.$router.push({name: "operations", params: {id: "new"}})
    },
    openEditDialog (operation) {
      this.$router.push({name: "operations", params: {id: operation.id}})
    },
    openDeleteDialog (operation) {
      this.selectedObject.id = operation.id
      this.selectedObject.name = operation.name
      this.showDeleteDialog = true
    },
    async saveEditDialog () {
      this.saving = true
      const data = {
        name: this.selectedObject.name,
        projects: this.selectedObject.projects,
      }
      const isNewObject = this.selectedObject.id === null
      if (isNewObject) {
        data.tenant = this.selectedTenant
      }
      try {
        const r =
          !isNewObject ?
          await api(endpoints.update_operation(this.selectedObject.id), data, {method: "PATCH"}) :
          await api(endpoints.create_operation(), data, {method: "POST"})
        if (r.ok) {
          this.$router.push({name: "operations"})
          await this.fetchItems()
        } else {
          // TODO: error handling
          this.$set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    async deleteOperation () {
      this.saving = true
      try {
        const r = await api(endpoints.delete_operation(this.selectedObject.id), undefined, {method: "DELETE"})
        if (r.ok) {
          this.showDeleteDialog = false
          await this.fetchItems()
        } else {
          // TODO: error handling
          this.$set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    getError (path) {
      const parts = path.split(".")
      let obj = this.errors.validation
      while (parts.length > 0) {
        const key = parts.splice(0, 1)
        obj = obj[key]
        if (typeof obj === "undefined") {
          return obj
        }
      }
      return obj
    },
    async fetchProjects () {
      if (this.selectedTenant === null) {
        return
      }
      this.projectsLoading = true
      try {
        const r = await api(endpoints.fetch_project(), {tenant: this.selectedTenant, ordering: "name"}, {method: "GET"})
        if (r.ok) {
          const d = await r.json()
          this.allProjects.splice(0, this.allProjects.length, ...d)
        } else {
          // TODO: error handling
        }
      } catch (err) {
          // TODO: error handling
      } finally {
        this.projectsLoading = false
      }
    },
  },
}
</script>

<style>
</style>
